// src/talent/components/AddResumeProjectSectionDialog.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

const predefinedHeadings = [
  "THEATRE",
  "FILM",
  "TELEVISION",
  "COMMERCIALS",
  "VIDEO",
  "LIVE EVENTS",
  "MODELING",
  "DANCE",
  "COMEDY / IMPROV",
  "VOICE-OVER",
];

const AddResumeProjectSectionDialog = ({
  open,
  onClose,
  userId,
  onSuccess,
  existingHeadings = [],
}) => {
  const [mode, setMode] = useState("predefined");
  const [sectionTitle, setSectionTitle] = useState("");
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);

  const normalizedExisting = existingHeadings.map((h) =>
    h.trim().toUpperCase()
  );

  const handleClose = () => {
    setSectionTitle("");
    setError("");
    setMode("predefined");
    setLoading(false);
    onClose();
  };

  const handleSave = async () => {
    const trimmed = sectionTitle.trim();
    if (!trimmed) {
      setError("Section title is required.");
      return;
    }
    if (trimmed.length > 50) {
      setError("Section title must be 50 characters or less.");
      return;
    }
    if (
      mode === "predefined" &&
      normalizedExisting.includes(trimmed.toUpperCase())
    ) {
      setError("This section has already been added.");
      return;
    }

    setLoading(true);
    try {
      const sectionPayload = {
        resumeSectionId: 0,
        sectionHeading: trimmed,
        sectionSortOrder: 0,
        userId: Number(userId),
        userBy: Number(userId),
        activeFlag: true,
      };

      // Step 1: just create the section
      await api.post(ENDPOINTS.saveResumeSection(), sectionPayload);

      setSnackbar({
        open: true,
        message: "Section added successfully!",
        severity: "success",
      });

      handleClose();
      onSuccess?.(); // refresh parent
    } catch (err) {
      console.error("Error adding section:", err);
      setSnackbar({
        open: true,
        message: "Something went wrong while adding the section.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const available = predefinedHeadings.filter(
    (h) => !normalizedExisting.includes(h.toUpperCase())
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New Category for Credits</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt:"6px" }}>
            <InputLabel>Section Type</InputLabel>
            <Select
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setSectionTitle("");
                setError("");
              }}
              label="Section Type"
            >
              <MenuItem value="predefined">
                Theatre, Film, Television, etc.
              </MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>

          {mode === "predefined" ? (
            <FormControl fullWidth>
              <InputLabel>Choose a category</InputLabel>
              <Select
                value={sectionTitle}
                onChange={(e) => {
                  setSectionTitle(e.target.value);
                  setError("");
                }}
                label="Predefined Section"
                error={!!error}
              >
                {available.length > 0 ? (
                  available.map((heading, idx) => (
                    <MenuItem key={idx} value={heading}>
                      {heading}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    All predefined sections used
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              label="Custom Section Title"
              value={sectionTitle}
              onChange={(e) => {
                setSectionTitle(e.target.value);
                setError("");
              }}
              error={!!error}
              helperText={error}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddResumeProjectSectionDialog;
